import {
  Box,
  Button,
  EncodingCountersBox,
  FormSchemaForm,
  FormSchemaFormField,
  FormSchemaTextField,
  InfoBox,
  Spacer
} from 'core/components'
import {
  EncodingForm as Base,
  DEFAULT_PRODUCTION_ORDER_ATTRIBUTES,
  EncodingFormState
} from 'core/components/encoding/EncodingForm'
import { FormSchemaField, FormSchemaFieldData } from 'core/shared/FormSchema'
import { __, T } from 'core/shared/i18n'
import { get as _get, set as _set } from 'lodash'
import { createRef } from 'react'
import {
  getInitialType,
  isCanEncodeWithOverQuantity,
  isCheckEncodedQuantity,
  isOrderCompleted,
  showCountersOnEncoding
} from '../shared/utils'

type ScanType = 'sku' | 'orderRow' | undefined

type CustomEncodingFormForOrderRowInitialTypeWithByOrderAndSkuState = EncodingFormState & {
  inputsKey: number
  orderRowDefaultValue?: string
  skuDefaultValue?: string
  extraFormValues: Record<string, FormSchemaFieldData>
}

export class EncodingForm extends Base<CustomEncodingFormForOrderRowInitialTypeWithByOrderAndSkuState> {
  private readonly orderRowInputRef = createRef<HTMLInputElement>()
  private readonly skuInputRef = createRef<HTMLInputElement>()

  state: CustomEncodingFormForOrderRowInitialTypeWithByOrderAndSkuState = {
    ...this.state,
    inputsKey: 0,
    orderRowDefaultValue: undefined,
    skuDefaultValue: undefined,
    extraFormValues: {}
  } as CustomEncodingFormForOrderRowInitialTypeWithByOrderAndSkuState

  private readonly baseClearProductInfo = this.clearProductInfo

  clearProductInfo = () => {
    this.baseClearProductInfo?.()
    this.resetDefaultValues()
  }

  private readonly usesGucciCustomOrderRowForm = (): boolean => {
    const mode = this.props.operation.options?.productionOrderRowSelectionMode ?? 'byOrderAndRowCode'

    if (this.initialType === 'orderRow' && mode === 'byCode') {
      // LG GS1 Scan and Pelletteria Campionario
      return true
    }

    if (this.initialType !== 'formSchema') {
      return false
    }

    const { schema } = this.state
    const isOrderRowFormSchema = schema.some(
      (field) => field.initialType === 'orderRow' || field.referenceInitialType === 'orderRow'
    )

    if (
      schema.some((field) => field.isProductCode || field.initialType === 'product') ||
      schema.some((field) => field.initialType === 'order')
    ) {
      return false
    }

    // LG GS1 Scan con Scelta
    return isOrderRowFormSchema
  }

  private readonly isExtraFormField = (field: FormSchemaField) =>
    !field.hide &&
    !field.isProductCode &&
    !['productionOrder', 'productionOrderRow'].includes(field.name) &&
    !['product', 'order', 'orderRow'].includes(field.initialType ?? '') &&
    !['product', 'order', 'orderRow'].includes(field.referenceInitialType ?? '')

  private readonly extraFieldValue = (field: FormSchemaField) =>
    _get(this.state.extraFormValues, field.name) ?? field.defaultValue

  private readonly extraFormData = () => {
    const data: Record<string, FormSchemaFieldData> = {}
    if (this.initialType !== 'formSchema') return data
    this.state.schema.filter(this.isExtraFormField).forEach((field) => {
      const value = this.extraFieldValue(field)
      if (value !== undefined && value !== '') {
        _set(data, field.name, value)
      }
    })
    return data
  }

  private readonly resetDefaultValues = () => {
    this.setState({
      orderRowDefaultValue: undefined,
      skuDefaultValue: undefined,
      extraFormValues: {}
    })
  }

  private readonly focusInput = (inputRef: React.RefObject<HTMLInputElement>, fallbackSelector?: string) => {
    const focus = () => {
      inputRef.current?.focus()
      if (!inputRef.current && fallbackSelector) {
        document.querySelector<HTMLInputElement>(fallbackSelector)?.focus()
      }
    }

    requestAnimationFrame(() => {
      focus()
      setTimeout(focus, 50)
    })
  }

  private readonly restoreOrderRowInputFocus = () => {
    this.focusInput(this.orderRowInputRef, '#productionOrderRowSku')
  }

  private readonly handleExtraFieldChange = (field: FormSchemaField, value: unknown) => {
    this.setState(
      (prevState) => {
        const extraFormValues = { ...prevState.extraFormValues }
        _set(extraFormValues, field.name, value)
        return { extraFormValues }
      },
      () => {
        if (field.type === 'select') {
          this.restoreOrderRowInputFocus()
        }
      }
    )
  }

  onSubmitError = (_error: any) => {
    this.resetDefaultValues()
  }

  formSchemaProductionOrderRow() {
    const initialType = getInitialType(this.props.operation)
    const { disableInput, operation, counters, productionOrderDetail } = this.props
    const { productionOrder, productionOrderRow, loadingCounterProductionOrder, loadingProductionOrderRow, schema } =
      this.state
    const displayProductionOrder = productionOrder ?? productionOrderRow?.productionOrder
    let tmp = schema
    if (this.initialType === 'formSchema') {
      tmp = tmp.filter((el) => el.initialType === 'orderRow' || el.referenceInitialType === 'orderRow')
    }
    return (
      <Box flex>
        {displayProductionOrder && (
          <>
            <InfoBox
              title={__(T.misc.production_order)}
              subtitle={displayProductionOrder?.code}
              data={displayProductionOrder}
              fields={this.getProductionOrderFields()}
              showMore={
                this.productionOrderFields.length > DEFAULT_PRODUCTION_ORDER_ATTRIBUTES
                  ? () => this.managerViewProductionOrderAttribute()
                  : undefined
              }
              viewMore={this.state.viewProductionOrderAttribute}
              onCloseClick={this.clearProductionOrderInfo}
            />
            <Spacer height={'10'} />
            {!productionOrderRow && loadingCounterProductionOrder && (
              <>
                {this.renderLoading()}
                <Spacer height={'10'} />
              </>
            )}
            {(!productionOrderRow || loadingProductionOrderRow) &&
              counters &&
              showCountersOnEncoding(initialType, counters) &&
              !loadingCounterProductionOrder && (
                <>
                  <EncodingCountersBox counters={counters} productionOrderDetail={productionOrderDetail} />
                  <Spacer height={'10'} />
                </>
              )}
          </>
        )}
        {!!productionOrderRow && !loadingProductionOrderRow && this.productInfoBox()}
        {loadingProductionOrderRow && this.renderLoading()}
        {!productionOrderRow &&
          !loadingProductionOrderRow &&
          !isOrderCompleted(counters, isCheckEncodedQuantity(operation), isCanEncodeWithOverQuantity(operation)) && (
            <>
              {this.usesGucciCustomOrderRowForm() ? (
                this.gucciCustomEncodingFormForOrderRowInitialTypeWithByOrderAndSku()
              ) : (
                <FormSchemaForm
                  key="formProductionOrderRow"
                  flex
                  style={{ justifyContent: 'space-between' }}
                  schema={tmp}
                  onSubmit={this.submitProductionOrderRow}
                  submitText={__(T.misc.next)}
                  disabled={disableInput}
                  operation={operation}
                />
              )}
            </>
          )}
      </Box>
    )
  }

  private readonly isValidSku = (value?: string) => !!value && /^\d{10}$/.test(value)

  private readonly getScanType = (value?: string): ScanType => {
    if (!value) return undefined
    return this.isValidSku(value) ? 'sku' : 'orderRow'
  }

  private readonly inputsAreInNormalFormat = (sku: string, orderRow: string) => {
    if (!sku && !orderRow) return false
    if (sku && orderRow) return this.isValidSku(sku) && !this.isValidSku(orderRow)
    if (sku) return this.isValidSku(sku)
    return !this.isValidSku(orderRow)
  }

  private readonly handleOrderSkuType = (
    orderRowType: ScanType,
    skuType: ScanType,
    orderRowInputRef: React.RefObject<HTMLInputElement>,
    skuInputRef: React.RefObject<HTMLInputElement>,
    sourceField: 'orderRow' | 'sku' | 'submit' = 'submit'
  ) => {
    if (orderRowType && skuType && orderRowType === skuType) {
      // Ignore duplicate type scans (e.g. scan sku twice or order row twice).
      if (sourceField === 'orderRow' && orderRowInputRef.current) {
        orderRowInputRef.current.value = ''
        this.focusInput(orderRowInputRef, '#productionOrderRowSku')
        return false
      }
      if (sourceField === 'sku' && skuInputRef.current) {
        skuInputRef.current.value = ''
        this.focusInput(skuInputRef, '#productionOrderSku')
        return false
      }
      return false
    }
    return true
  }

  gucciCustomEncodingFormForOrderRowInitialTypeWithByOrderAndRowOnEnterPressed = (
    orderRowInputRef: React.RefObject<HTMLInputElement>,
    skuInputRef: React.RefObject<HTMLInputElement>,
    setOrderRowDefaultValue: (value: string) => void,
    setSkuDefaultValue: (value: string) => void,
    incrementInputsKey: () => void,
    sourceField: 'orderRow' | 'sku' | 'submit' = 'submit'
  ) => {
    const orderRowInputValue = orderRowInputRef.current?.value?.trim()
    const skuInputValue = skuInputRef.current?.value?.trim()

    let orderRow = orderRowInputValue
    let sku = skuInputValue

    if ((!!orderRow && !sku) || (!orderRow && !!sku)) {
      // first of the 2 inputs
      if (!this.inputsAreInNormalFormat(sku, orderRow)) {
        orderRow = skuInputValue
        sku = orderRowInputValue
        setOrderRowDefaultValue(skuInputValue ?? '')
        setSkuDefaultValue(orderRowInputValue ?? '')
        incrementInputsKey()
      }
      if (!orderRow) {
        this.focusInput(orderRowInputRef, '#productionOrderRowSku')
        return
      } else if (!sku) {
        this.focusInput(skuInputRef, '#productionOrderSku')
        return
      }
    } else {
      // both inputs are filled

      const orderRowType: ScanType = this.getScanType(orderRow)
      const skuType: ScanType = this.getScanType(sku)
      const check = this.handleOrderSkuType(orderRowType, skuType, orderRowInputRef, skuInputRef, sourceField)
      if (!check) return

      // still, check fields (normal or inverted) because user can press next without press enter
      if (!this.inputsAreInNormalFormat(sku, orderRow)) {
        orderRow = skuInputValue
        sku = orderRowInputValue
      }
      orderRow = this.checkOrderRowYear(orderRow)
      this.submitProductionOrderRow({
        ...this.extraFormData(),
        productionOrder: orderRow,
        productionOrderRow: `${orderRow}${sku}`
      })
    }
  }

  /**
   * L'anno a cui corrisponde la cifra dipende dall'anno in corso.
   * Per garantire la maggiore finestra possibile, valgono 8 anni
   * precedenti, l'anno attuale e l'anno prossimo.
   *   Per cui:
   *     2020 → 2012 - 2021
   *     2021 → 2013 - 2022
   *     2022 → 2014 - 2023
   *     2023 → 2015 - 2024
   *     2024 → 2016 - 2025
   *     2025 → 2017 - 2026
   *     2026 → 2018 - 2027
   *     2027 → 2019 - 2028
   *     2028 → 2020 - 2029
   *     2029 → 2021 - 2030
   */
  private readonly getClosestYearFromDigit = (yearDigit: number): number => {
    const now = new Date()
    const base = now.getUTCFullYear() - 8
    return base + ((yearDigit - (base % 10) + 10) % 10)
  }

  checkOrderRowYear = (orderRow: string) => {
    if (orderRow.length === 11 || orderRow.length === 12 || orderRow.length === 13) {
      // Commessa non estesa: cifra anno in posizione 4, campo row a 1, 2 o 3 caratteri
      const yearUnit = Number.parseInt(orderRow[4])
      const year = this.getClosestYearFromDigit(yearUnit)
      return `${orderRow.slice(0, 4)}${year}${orderRow.slice(5)}`
    }
    if (orderRow.length === 15 && /^\d/.test(orderRow[0])) {
      // Barcode commessa Zegna a 15 cifre: cifra anno in posizione 0
      const yearUnit = Number.parseInt(orderRow[0])
      const year = this.getClosestYearFromDigit(yearUnit)
      return `${year}${orderRow.slice(1)}`
    }
    // Commessa già estesa (14, 15 o 16 cifre): nessuna trasformazione
    return orderRow
  }

  gucciCustomEncodingFormForOrderRowInitialTypeWithByOrderAndSku = () => {
    const inputsKey = this.state.inputsKey
    const orderRowInputRef = this.orderRowInputRef
    const skuInputRef = this.skuInputRef
    const extraFields = this.initialType === 'formSchema' ? this.state.schema.filter(this.isExtraFormField) : []

    return (
      <Box style={{ display: 'flex', flex: 1, flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {extraFields.map((field, index) => (
            <FormSchemaFormField
              key={`${field.name}-${inputsKey}`}
              field={field}
              index={index}
              value={this.extraFieldValue(field)}
              defaultValue={field.defaultValue}
              onChange={(value) => this.handleExtraFieldChange(field, value)}
              disabled={this.props.disableInput}
            />
          ))}
          <FormSchemaTextField
            inputRef={orderRowInputRef}
            key={`order-row-${inputsKey}`}
            field={{
              type: 'text',
              name: 'productionOrderRowSku',
              label: __(T.misc.production_order_row),
              required: true,
              autoFocus: true // focus should be on order row input regardless of extra fields
            }}
            index={extraFields.length}
            value={undefined}
            defaultValue={this.state.orderRowDefaultValue}
            onChange={(_value) => {}}
            onEnter={() =>
              this.gucciCustomEncodingFormForOrderRowInitialTypeWithByOrderAndRowOnEnterPressed(
                orderRowInputRef,
                skuInputRef,
                (value) => this.setState({ orderRowDefaultValue: value }),
                (value) => this.setState({ skuDefaultValue: value }),
                () => this.setState({ inputsKey: this.state.inputsKey + 1 }),
                'orderRow'
              )
            }
          />
          <FormSchemaTextField
            inputRef={skuInputRef}
            key={`sku-${inputsKey}`}
            field={{
              type: 'text',
              name: 'productionOrderSku',
              label: __(T.misc.production_order_sku),
              required: true,
              autoFocus: false
            }}
            index={extraFields.length + 1}
            value={undefined}
            defaultValue={this.state.skuDefaultValue}
            onChange={(_value) => {}}
            onEnter={() =>
              this.gucciCustomEncodingFormForOrderRowInitialTypeWithByOrderAndRowOnEnterPressed(
                orderRowInputRef,
                skuInputRef,
                (value) => this.setState({ orderRowDefaultValue: value }),
                (value) => this.setState({ skuDefaultValue: value }),
                () => this.setState({ inputsKey: this.state.inputsKey + 1 }),
                'sku'
              )
            }
          />
        </Box>
        <Button
          onClick={() =>
            this.gucciCustomEncodingFormForOrderRowInitialTypeWithByOrderAndRowOnEnterPressed(
              orderRowInputRef,
              skuInputRef,
              (value) => this.setState({ orderRowDefaultValue: value }),
              (value) => this.setState({ skuDefaultValue: value }),
              () => this.setState({ inputsKey: this.state.inputsKey + 1 }),
              'submit'
            )
          }
        >
          {__(T.misc.next)}
        </Button>
      </Box>
    )
  }
}
